<!--
/**
 * @copyright   OnlyOneProd © 2025
 * @authors     Jonathan COULLET <contact@onlyoneprod.com>
 * @link        https://onlyoneprod.com
 * @notice      All information contained herein is,
 *              and remains the property of OnlyOneProd and its suppliers,
 *              Any dissemination of this information or reproduction
 *              of this material is strictly forbidden unless prior
 *              written permission is obtained from OnlyOneProd.
 */
-->

<template>
    <div class="input-group">
        <textarea v-if="inputType == 'area'" :class="{ focussed: focussed }" class="peer" :id="name" :name="name"
            :autocomplete="autocomplete" :required="required ? true : false" :aria-required="required || false"
            placeholder=" " @blur="handleBlur" @input="updateValue" :disabled="disabled" :value="value"></textarea>
        <input v-else :type="inputType" :class="{ focussed: focussed }" class="peer" :id="name" :name="name"
            :autocomplete="autocomplete" :required="required ? true : false" :aria-required="required || false"
            placeholder=" " @blur="handleBlur" @input="updateValue" :disabled="disabled" :value="value" />
        <label v-if="label" :for="name"
            class="peer-focus:-translate-y-3 peer-focus:translate-x-1 peer-focus:scale-75 peer-focus:font-medium peer-focus:start-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-1 peer-placeholder-shown:translate-x-0 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4">{{
                label }}<sup v-if="required" class="required">*</sup></label>
        <p v-if="invalid" :class="{ 'never-focussed': !focussed, disabled: disabled }"
            class="invalid peer-invalid:h-4 peer-invalid:opacity-100">
            {{ invalid }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, defineProps, ref } from "vue";

const props = defineProps<{
    name: string;
    type?: "text" | "email" | "tel" | "url" | "password" | "area";
    value?: string;
    label?: string;
    autocomplete?: string;
    invalid?: string;
    required?: boolean;
    disabled?: boolean;
}>();
const emit = defineEmits(["update:value"]);

const focussed = ref(false);
const inputType = computed(() => props.type || "text");

const handleBlur = () => (focussed.value = true);
const updateValue = (event: Event) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target) {
        return;
    }
    emit("update:value", target.value);
};
</script>

<style lang="sass">
.input-group
    @apply relative z-0 w-full mb-2

    input
        @apply h-12 pt-4

    textarea
        @apply h-40 py-4

    input, textarea
        &.focussed:invalid
            @apply border-orange

    label
        @apply absolute origin-[0] top-2.5 px-4 duration-300 transform -translate-y-3 translate-x-1 scale-75 font-medium start-0 text-bone

        .required
            @apply text-orange ml-0.5

    p.invalid
        @apply block text-orange px-4 h-0 text-sm opacity-0 duration-300 transform

        &.never-focussed
            @apply h-0 opacity-0
</style>
